import React, { useState } from "react";
import { IoChevronDown, IoChevronUp, IoLogoAmazon, IoLogoGoogle } from "react-icons/io5";
import { ROLE_OPTIONS, SHARE_OPTIONS, devicesLabel } from "./shareUtils";

// The app's Accordion (app/components/Accordion.js): a coloured title with a
// chevron that shows or hides what's under it. Open to start with.
export const Section = ({ id, title, as: Heading = "h2", className = "", children }) => {
  const [open, setOpen] = useState(true);
  return (
    <section className={`hx-share-section ${className}`}>
      <Heading className="hx-share-section-head">
        <button type="button" aria-expanded={open} aria-controls={`${id}-body`} onClick={() => setOpen(!open)}>
          <span>{title}</span>
          {open ? <IoChevronUp aria-hidden="true" /> : <IoChevronDown aria-hidden="true" />}
        </button>
      </Heading>
      <div id={`${id}-body`} className="hx-share-section-body" hidden={!open}>
        {children}
      </div>
    </section>
  );
};

// A person in a list: the initials in a purple circle, a name and up to two
// more lines.
export const Person = ({ initials, name, detail, note }) => (
  <>
    <span className="hx-share-avatar" aria-hidden="true">
      {initials}
    </span>
    <span className="hx-share-person-text">
      <span className="hx-share-person-name">{name}</span>
      {detail ? <span className="hx-share-person-detail">{detail}</span> : null}
      {note ? <span className="hx-share-person-detail">{note}</span> : null}
    </span>
  </>
);

// The app's VoiceAssistantToggles: whether the person's Google Home and Alexa
// get the shared devices (shareWithGoogle and shareWithAlexa on the share). Only
// the property's creator can change them; members see their own read-only.
export const VoiceToggles = ({ idPrefix, google, alexa, onGoogle, onAlexa, readOnly = false }) => {
  const rows = [
    { key: "google", Icon: IoLogoGoogle, title: "Google Home", value: google, onChange: onGoogle },
    { key: "alexa", Icon: IoLogoAmazon, title: "Amazon Alexa", value: alexa, onChange: onAlexa },
  ];
  return (
    <fieldset className={`hx-share-voice ${readOnly ? "is-readonly" : ""}`} aria-describedby={`${idPrefix}-voice-note`}>
      <legend className="hx-share-voice-label">Voice assistants</legend>
      <p id={`${idPrefix}-voice-note`} className="hx-share-voice-note">
        {readOnly ? "Set by the property owner." : "Show the shared devices in this user's Google Home and Alexa."}
      </p>
      {rows.map(({ key, Icon, title, value, onChange }) => (
        <label key={key} className="hx-share-voice-row" htmlFor={`${idPrefix}-${key}`}>
          <Icon aria-hidden="true" />
          <span className="hx-share-voice-title">{title}</span>
          <span className="hx-share-voice-switch">
            <input
              id={`${idPrefix}-${key}`}
              type="checkbox"
              role="switch"
              checked={value}
              disabled={readOnly}
              onChange={(event) => onChange?.(event.target.checked)}
            />
            <span className={`hx-switch ${value ? "is-on" : ""}`} aria-hidden="true">
              <span className="hx-switch-knob" />
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  );
};

// The app's Dropdown (app/components/DropDown.js): the choice on an underline
// with a chevron, "Select an option" until something is picked.
const SelectField = ({ id, label, value, options, onChange }) => (
  <div className="hx-field">
    <label className="hx-field-label" htmlFor={id}>
      {label}
    </label>
    <div className="hx-share-select">
      <select
        id={id}
        className={`hx-field-input ${value === "" ? "is-empty" : ""}`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {value === "" ? (
          <option value="" disabled>
            Select an option
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <IoChevronDown aria-hidden="true" />
    </div>
  </div>
);

// Role, "Choose what to share" and, for chosen devices, the button that opens
// Select Devices. Invite and Update Sharing both have them.
export const ShareOptions = ({ idPrefix, role, onRole, shareAll, onShareAll, deviceCount, onPickDevices }) => (
  <>
    <SelectField id={`${idPrefix}-role`} label="Role" value={role} options={ROLE_OPTIONS} onChange={onRole} />
    <SelectField
      id={`${idPrefix}-what`}
      label="Choose what to share"
      value={shareAll ? "all" : "some"}
      options={SHARE_OPTIONS}
      onChange={(value) => onShareAll(value === "all")}
    />
    {shareAll ? null : (
      <>
        <button type="button" className="hx-share-btn is-light" onClick={onPickDevices}>
          {deviceCount > 0 ? `${devicesLabel(deviceCount)} Selected` : "Select Devices"}
        </button>
        {deviceCount > 0 ? null : <p className="hx-share-hint">Select at least one device, or choose Every Device.</p>}
      </>
    )}
  </>
);
